var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import styled, { css } from 'styled-components';
import * as icons from '@boxine/tonies-ui/icons';
import PropTypes from 'prop-types';
import { FocusStyles } from '../../../styles/FocusStyles';
import { variables, theme } from '../../themes/theme';
import Icon from '../../Icon';
import { ErrorMessage } from '../../ErrorMessage';
import { SIZE_TYPES } from '../../Input';
const StyledLabel = styled.label `
  color: ${variables.Gray};
  background-color: ${variables.White};
  font-size: 0.825rem;
  font-family: ${props => props.theme.fonts.serif};
  position: relative;
  display: inline-block;

  ${props => props.message &&
    css `
      ${props => {
        if (props.size === 'default') {
            return css `
            padding-bottom: 10px;
          `;
        }
        if (props.size === 'small') {
            return css `
            padding-bottom: 12px;
          `;
        }
        if (props.size === 'large') {
            return css `
            padding-bottom: 14px;
          `;
        }
    }}
    `}

  .select-inner {
    position: relative;
    display: flex;
    align-items: center;
    border: 1px solid ${theme.DirtyWhite};
    border-radius: 0;

    ${props => {
    if (props.size === 'default') {
        return css `
          margin-bottom: 0.9rem;
        `;
    }
    if (props.size === 'small') {
        return css `
          margin-bottom: 0.1875rem;
        `;
    }
    if (props.size === 'large') {
        return css `
          margin-bottom: 1rem;
        `;
    }
}}

    ${props => props.invalid &&
    css `
        border-color: ${theme.BrandPrimary};
      `}


    select {
      font-family: ${props => props.theme.fonts.serif};
    }
    .icon {
      position: absolute;
      right: 0.5rem;
      width: 0.75rem;

      top: ${props => {
    switch (props.size) {
        case 'small':
            return 0;
        case 'large':
            return '0.125rem';
        default:
            return '0.0625rem';
    }
}};

      svg {
        width: 100%;
      }
    }
  }
`;
const StyledSelect = styled.select `
  appearance: none;
  border: 0;
  flex-grow: 1;
  background-color: ${variables.Transparent};
  z-index: 6;
  padding: 0.4375rem 4.125rem 0.4375rem 0.5625rem;
  line-height: 1.38;
  color: ${props => props.theme.colors.darkergrey};

  ${props => props.disabled &&
    css `
      cursor: -not-allowed;
    `}

  ${props => {
    if (props.size === 'default') {
        return css `
        font-size: 1rem;
      `;
    }
    if (props.size === 'small') {
        return css `
        font-size: 0.875rem;
      `;
    }
    if (props.size === 'large') {
        return css `
        font-size: 1.125rem;
      `;
    }
}}

  ${FocusStyles}
`;
const StyledIcon = styled(Icon) `
  z-index: 5;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
`;
// eslint-disable-next-line react/require-default-props
const Select = (_a) => {
    var { dataTestId, dataTestIdErrorMessage, label } = _a, props = __rest(_a, ["dataTestId", "dataTestIdErrorMessage", "label"]);
    return (React.createElement(StyledLabel, { className: props.className, message: props.message, size: props.size, invalid: props.invalid, "data-testid": "select-label" },
        label,
        React.createElement("div", { className: "select-inner" },
            React.createElement(StyledSelect, Object.assign({}, props, { "data-testid": dataTestId, value: props.value }), props.options.map(option => (React.createElement("option", { "data-testid": "select-option", key: option.value, value: option.value }, option.label)))),
            props.disabled && (React.createElement(StyledIcon, { type: icons.arrowDown, height: "12", fill: variables.DirtyWhite, shouldBeHiddenFromSR: Boolean(label) })),
            !props.disabled && (React.createElement(StyledIcon, { type: icons.arrowDown, height: "12", fill: variables.Gray, shouldBeHiddenFromSR: !!label }))),
        props.message && (React.createElement(ErrorMessage, { dataTestId: dataTestIdErrorMessage }, props.message.message))));
};
Select.propTypes = {
    /**
     * Used for styled components to forward style properties
     */
    className: PropTypes.string,
    dataTestId: PropTypes.string,
    dataTestIdErrorMessage: PropTypes.string,
    options: PropTypes.array.isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    size: PropTypes.oneOf(SIZE_TYPES),
    label: PropTypes.node,
    invalid: PropTypes.bool,
    message: PropTypes.shape({
        message: PropTypes.string,
    }),
};
Select.defaultProps = {
    className: '',
    dataTestId: null,
    dataTestIdErrorMessage: null,
    disabled: false,
    label: '',
    message: null,
    size: 'default',
    invalid: false,
    value: '',
    // not a useful default, but okay to get rid of warnings
    onChange: () => undefined,
};
export { Select };
